import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, PenToolIcon, UsersIcon, CalendarIcon, GraduationCapIcon, MonitorIcon, SparklesIcon, ClockIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import useDarkClasses from '../hooks/useDarkClasses';
import useRoleTheme from '../hooks/useRoleTheme';
import { useUserRole } from '../context/UserRoleContext';
import EventCard from '../components/Events/EventCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import VendorEventsHub from './vendors/VendorEventsHub';
import { EVENT_TYPES } from '../data/eventSchema';
import type { FullEventData } from '../data/eventSchema';
import { mockEventDetails } from './event-detail/data';
const Events = () => {
  const {
    isReader
  } = useUserRole();
  if (!isReader) {
    return <VendorEventsHub data-id="element-9789" data-mp="1wsn1plzreeql" />;
  }
  return <ReaderEvents data-id="element-9790" data-mp="1wuzi3upvch1j" />;
};
const getStatusBadge = (status: string) => {
  switch (status) {
    case 'upcoming':
      return {
        label: 'Upcoming',
        classes: 'bg-blue-100 text-blue-800'
      };
    case 'tickets-live':
      return {
        label: 'Tickets Live',
        classes: 'bg-green-100 text-green-800'
      };
    case 'sold-out':
      return {
        label: 'Sold Out',
        classes: 'bg-red-100 text-red-800'
      };
    case 'postponed':
      return {
        label: 'Postponed',
        classes: 'bg-amber-100 text-amber-800'
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        classes: 'bg-red-100 text-red-800'
      };
    case 'completed':
      return {
        label: 'Completed',
        classes: 'bg-gray-100 text-gray-600'
      };
    default:
      return {
        label: status,
        classes: 'bg-gray-100 text-gray-600'
      };
  }
};
const formatTime = (timeStr: string) => {
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};
const getEventTypeLabel = (eventType: string) => {
  return EVENT_TYPES.find(t => t.value === eventType)?.label || eventType;
};
const getLowestPrice = (event: FullEventData) => {
  if (!event.accessRules.ticketRequired || event.accessRules.ticketTiers.length === 0) return null;
  const available = event.accessRules.ticketTiers.filter(t => !t.soldOut);
  if (available.length === 0) {
    return {
      price: event.accessRules.ticketTiers[0].price,
      currency: event.accessRules.ticketTiers[0].currency,
      allSoldOut: true
    };
  }
  const cheapest = available.reduce((min, t) => t.price < min.price ? t : min, available[0]);
  return {
    price: cheapest.price,
    currency: cheapest.currency,
    allSoldOut: false
  };
};
const mapEventToCardProps = (event: FullEventData) => {
  const startDate = new Date(event.startDate);
  const dateStr = startDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const statusBadge = getStatusBadge(event.status);
  const isFree = !event.accessRules.ticketRequired;
  const priceInfo = getLowestPrice(event);
  let priceStr: string | undefined;
  if (isFree) {
    priceStr = 'Free';
  } else if (priceInfo) {
    priceStr = priceInfo.allSoldOut ? 'Sold Out' : `From ${priceInfo.price} ${priceInfo.currency}`;
  }
  const locationText = event.isVirtual && event.virtualDetails ? event.virtualDetails.platform || 'Virtual' : event.location ? `${event.location.venueName}, ${event.location.city}` : 'TBA';
  const country = event.isVirtual ? undefined : event.location?.country;
  return {
    title: event.eventName,
    date: dateStr,
    time: `${formatTime(event.startTime)} - ${formatTime(event.endTime)}`,
    type: getEventTypeLabel(event.eventType),
    description: event.shortDescription,
    image: event.coverImage || event.bannerImage || 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
    location: locationText,
    link: `/events/${event.id}`,
    eventId: parseInt(event.id),
    status: statusBadge,
    price: priceStr,
    isVirtual: event.isVirtual,
    country,
    ctaLabel: event.status === 'sold-out' ? 'Sold Out' : isFree ? 'RSVP' : 'Get Tickets',
    ctaDisabled: event.status === 'sold-out'
  };
};
const eventCategories = [{
  title: 'Author Events',
  description: 'Book signings, launches, talks, and panels',
  icon: <PenToolIcon className="h-7 w-7" data-id="element-9791" data-mp="28ucborehauz" />,
  image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
  subLinks: [{
    label: 'Book Signings',
    to: '/events/book-signings'
  }, {
    label: 'Book Launches',
    to: '/events/book-launches'
  }, {
    label: 'Author Talks',
    to: '/events/author-talks'
  }, {
    label: 'Author Panels',
    to: '/events/author-panels'
  }]
}, {
  title: 'Reader Community',
  description: 'Book clubs, swaps, meetups, and local events',
  icon: <UsersIcon className="h-7 w-7" data-id="element-9792" data-mp="3jr03pujvhudk" />,
  image: 'https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?w=800',
  subLinks: [{
    label: 'Local Authors',
    to: '/events/local-authors'
  }, {
    label: 'In-Person Clubs',
    to: '/events/book-clubs/in-person'
  }, {
    label: 'Virtual Clubs',
    to: '/events/book-clubs/virtual'
  }, {
    label: 'Silent Clubs',
    to: '/events/silent-book-clubs'
  }, {
    label: 'Book Swaps',
    to: '/events/book-swaps'
  }, {
    label: 'Meetups',
    to: '/events/reading-meetups'
  }]
}, {
  title: 'Large Community Events',
  description: 'Multi-author signings and conventions',
  icon: <CalendarIcon className="h-7 w-7" data-id="element-9793" data-mp="3536crxzgmze3" />,
  image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?w=800',
  subLinks: [{
    label: 'Multi-Author Signings',
    to: '/events/multi-author-signings'
  }, {
    label: 'Conventions & Festivals',
    to: '/events/conventions-festivals'
  }]
}, {
  title: 'Learning & Industry',
  description: 'Writing, publishing, and craft workshops',
  icon: <GraduationCapIcon className="h-7 w-7" data-id="element-9794" data-mp="3nt4m0hezuyjb" />,
  image: 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
  subLinks: [{
    label: 'Writing Workshops',
    to: '/events/writing-workshops'
  }, {
    label: 'Publishing Workshops',
    to: '/events/publishing-workshops'
  }, {
    label: 'Craft Masterclasses',
    to: '/events/craft-masterclasses'
  }]
}, {
  title: 'Online Events',
  description: 'Virtual author events, livestreams, and workshops',
  icon: <MonitorIcon className="h-7 w-7" data-id="element-9795" data-mp="1zvwrddc7qk3m" />,
  image: 'https://images.unsplash.com/photo-1599687266725-0d4d52716b06?w=800',
  subLinks: [{
    label: 'Virtual Author Events',
    to: '/events/virtual-author-events'
  }, {
    label: 'Livestream Discussions',
    to: '/events/livestream-discussions'
  }, {
    label: 'Online Workshops',
    to: '/events/online-workshops'
  }]
}, {
  title: 'Special Events',
  description: 'Reading retreats and midnight releases',
  icon: <SparklesIcon className="h-7 w-7" data-id="element-9796" data-mp="2dw7h2d1sdsn7" />,
  image: 'https://images.unsplash.com/photo-1519682577862-22b62b24e493?w=800',
  subLinks: [{
    label: 'Reading Retreats',
    to: '/events/reading-retreats'
  }, {
    label: 'Midnight Releases',
    to: '/events/midnight-releases'
  }]
}];
const ReaderEvents = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [savedEvents, setSavedEvents] = useState<number[]>([]);
  const allEvents: FullEventData[] = mockEventDetails;
  const upcomingEvents = allEvents;
  const virtualEvents = allEvents.filter(e => e.isVirtual);
  const toggleSave = (id: number) => {
    setSavedEvents(prev => prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]);
  };
  const filterSections: FilterSection[] = [{
    id: 'type',
    title: 'Event Type',
    type: 'checkbox',
    options: [{
      value: 'signing',
      label: 'Book Signing'
    }, {
      value: 'launch',
      label: 'Book Launch'
    }, {
      value: 'talk',
      label: 'Author Talk'
    }, {
      value: 'panel',
      label: 'Author Panel'
    }, {
      value: 'club',
      label: 'Book Club'
    }, {
      value: 'workshop',
      label: 'Workshop'
    }, {
      value: 'convention',
      label: 'Convention'
    }, {
      value: 'virtual',
      label: 'Virtual Event'
    }, {
      value: 'retreat',
      label: 'Reading Retreat'
    }],
    value: [],
    onChange: () => {}
  }, {
    id: 'format',
    title: 'Format',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Formats'
    }, {
      value: 'in-person',
      label: 'In-Person'
    }, {
      value: 'virtual',
      label: 'Virtual'
    }],
    value: 'all',
    onChange: () => {}
  }, {
    id: 'price',
    title: 'Price',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Prices'
    }, {
      value: 'free',
      label: 'Free'
    }, {
      value: 'paid',
      label: 'Paid'
    }],
    value: 'all',
    onChange: () => {}
  }, {
    id: 'age',
    title: 'Age Restriction',
    type: 'checkbox',
    options: [{
      value: 'none',
      label: 'None (All Ages)'
    }, {
      value: '16+',
      label: '16+'
    }, {
      value: '18+',
      label: '18+'
    }, {
      value: '21+',
      label: '21+'
    }],
    value: [],
    onChange: () => {}
  }];
  return <div className={`min-h-screen ${dk.page} pb-12`} data-id="element-9797" data-mp="kxoophls0ujb">
      {/* Header */}
      <div className={`${dk.surface} border-b ${dk.border} py-8`} data-id="element-9798" data-mp="fzep8ut0r7rw">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-9799" data-mp="2fb7geat4liwp">
          <h1 className={`text-3xl font-bold ${dk.textHeading} mb-3`} data-id="element-9800" data-mp="2f93efyesetz2">
            Book Events
          </h1>
          <p className={`text-lg ${dk.textSecondary} max-w-2xl mx-auto mb-8`} data-id="element-9801" data-mp="2itdapjrerpll">
            Discover book signings, author meets, workshops, and reading groups
            happening locally, globally, and online.
          </p>

          <div className="max-w-2xl mx-auto flex gap-3" data-id="element-9802" data-mp="2rlf9zepqqzua">
            <div className="relative flex-1" data-id="element-9803" data-mp="2ro1h5d8pzdbz">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-9804" data-mp="2wjqhvjwh709v" />
              <input type="text" placeholder="Search events, authors, or locations..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-9805" data-mp="1zwlvpmiwfwnc" />
            </div>
            <button onClick={() => setSidebarOpen(true)} className={`px-4 py-3 rounded-lg border flex items-center gap-2 ${dk.surface} ${dk.border} ${dk.textPrimary}`} data-id="element-9806" data-mp="3k9ukbnxxgtza">
              <FilterIcon className="h-5 w-5" data-id="element-9807" data-mp="2zjgzfs7tjdte" />
              <span className="hidden sm:inline" data-id="element-9808" data-mp="2umqk573u8m91">Filters</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9809" data-mp="39ig4z1xaetc6">
        <div className="flex gap-8" data-id="element-9810" data-mp="34ko7zthsch14">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9811" data-mp="34lpqgj14s5sv">
            <FilterSidebar sections={filterSections} onReset={() => {}} title="Filter Events" data-id="element-9812" data-mp="2qkn5k9lvh1jj" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0 space-y-12" data-id="element-9813" data-mp="2ls2dpdmj0yin">
            {/* Upcoming Events */}
            <section data-id="element-9814" data-mp="1y282f0huvi0i">
              <div className="flex items-center justify-between mb-6" data-id="element-9815" data-mp="1t5992k60nf3d">
                <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-9816" data-mp="1t357w9zi4u6s">
                  Upcoming Events
                </h2>
                <Link to="/events/all" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-9817" data-mp="ddxcg8w3rjdd">
                  View All
                </Link>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-9818" data-mp="3nkqqdcjxk5n">
                {upcomingEvents.map(event => {
                const props = mapEventToCardProps(event);
                return <EventCard key={event.id} {...props} isSaved={savedEvents.includes(props.eventId!)} onToggleSave={toggleSave} data-id="element-9819" data-mp="wd1h75gm2qz8" />;
              })}
              </div>
            </section>

            {/* Virtual Events */}
            {virtualEvents.length > 0 && <section data-id="element-9820" data-mp="3752x0gcoej3y">
                <div className="flex items-center justify-between mb-6" data-id="element-9821" data-mp="376e1yjhuxwwn">
                  <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-9822" data-mp="3nc8thjg7t5bd">
                    Virtual Events
                  </h2>
                  <Link to="/events/virtual-author-events" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-9823" data-mp="3ijo0gk54uw8w">
                    View All
                  </Link>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-9824" data-mp="a572e58ti2fk">
                  {virtualEvents.map(event => {
                const props = mapEventToCardProps(event);
                return <EventCard key={`v-${event.id}`} {...props} isSaved={savedEvents.includes(props.eventId!)} onToggleSave={toggleSave} data-id="element-9825" data-mp="6hvf7igm2e3" />;
              })}
                </div>
              </section>}

            {/* Browse by Category */}
            <section data-id="element-9826" data-mp="wenn7u8wyjvt">
              <h2 className={`text-2xl font-bold mb-6 ${dk.textHeading}`} data-id="element-9827" data-mp="wf5pb7i9bsbn">
                Browse by Category
              </h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-9828" data-mp="10ddb4grkvjtp">
                {eventCategories.map(category => <div key={category.title} className={`rounded-xl overflow-hidden shadow-sm border flex flex-col ${dk.card}`} data-id="element-9829" data-mp="1a4zig20soyr6">
                    <div className="relative h-36" data-id="element-9830" data-mp="1k3f6fw4izrwg">
                      <img src={category.image} alt={category.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-9831" data-mp="1k248icxrhed9" />
                      <div className="absolute inset-0 bg-black/50" data-id="element-9832" data-mp="1t20z4k3ponay" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 text-white" data-id="element-9833" data-mp="3pxoz0qaw4m3b">
                        <div className="mb-2" data-id="element-9834" data-mp="3uxks3ui1ut43">{category.icon}</div>
                        <h3 className="text-lg font-bold" data-id="element-9835" data-mp="1221gwz36f3yv">{category.title}</h3>
                        <p className="text-xs text-white/80 mt-1" data-id="element-9836" data-mp="x2ghknk3gtlm">
                          {category.description}
                        </p>
                      </div>
                    </div>
                    <div className="p-4" data-id="element-9837" data-mp="1623g91lfvlaz">
                      <div className="flex flex-wrap gap-2" data-id="element-9838" data-mp="1611wm8qcxqhn">
                        {category.subLinks.map(sub => <Link key={sub.to} to={sub.to} className={`inline-flex items-center text-xs px-3 py-1.5 rounded-full border transition-colors ${dk.border} ${dk.textSecondary} hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-300 dark:hover:border-indigo-800`} data-id="element-9839" data-mp="1kxj2wljowexc">
                            {sub.label}
                            <ArrowRightIcon className="h-3 w-3 ml-1" data-id="element-9840" data-mp="1yp61is13tzja" />
                          </Link>)}
                      </div>
                    </div>
                  </div>)}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
};
export default Events;